import { observePerformance, supportPerformanceTimingEvent } from './observePerformance';

/**
 * Executes a callback function when the First Contentful Paint (FCP) occurs.
 *
 * @important This function is intended for use by app shells only.
 *
 * @param {function(PerformanceEntry): unknown} callback - A function to be called with the FCP entry.
 *
 * @example
 * onFirstContentfulPaint((entry) => {
 *   console.log(`First Contentful Paint occurred at ${entry.startTime} ms`);
 *   // Perform post-FCP operations or measurements
 * });
 *
 * @remarks
 * - This function is designed for performance measurements only.
 * - It checks for browser support of the Performance API and FCP entries.
 * - If FCP has already occurred, the callback is scheduled using requestIdleCallback.
 * - If FCP hasn't occurred yet, it sets up a PerformanceObserver to wait for it.
 * - The callback is always executed in an idle period to avoid impacting page performance.
 * - In unsupported browsers, this function will silently do nothing.
 *
 * @see {@link https://web.dev/fcp/|First Contentful Paint (FCP)}
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/PerformancePaintTiming|PerformancePaintTiming}
 */
export const onFirstContentfulPaint = (callback: (entry: PerformanceEntry) => unknown): void => {
    if (!window.performance || !performance.getEntriesByName) {
        // unsupported browser
        return;
    }

    if (!supportPerformanceTimingEvent('first-contentful-paint')) {
        return;
    }
    const existingFcpEntry = performance.getEntriesByName('first-contentful-paint')[0];
    if (existingFcpEntry) {
        requestIdleCallback(() => callback(existingFcpEntry));
        return;
    }

    observePerformance(['paint'], (disconnectObserver, entry) => {
        requestIdleCallback(() => {
            callback(entry);
            disconnectObserver();
        });
    });
};
